import React, { useEffect, useRef, useState } from 'react';
import { useProCalculator } from '../hooks/useProCalculator';
import ProCalculatorFooter from './ProCalculatorFooter';
import SectionHelp from './SectionHelp';
import '../styles/CookieRowsSection.css';

const CookieRowsSection = () => {
  const {
    cookieRows,
    updateCookieRow,
    addCookieRow,
    removeCookieRow,
    isMobile,
    goToNextSection,
    activeSection,
    setActiveSection,
    loadedPlanName,
    skipSets,
    sortCookiesByName,
    reorderCookiesById,
  } = useProCalculator();

  // Refs for input elements and previous row count
  const inputRefs = useRef({});
  const prevCookieRowsLengthRef = useRef(cookieRows.length);

  // Drag-and-drop state (desktop only)
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  useEffect(() => {
    // Check if a row was added
    if (cookieRows.length > prevCookieRowsLengthRef.current) {
      const newRow = cookieRows[cookieRows.length - 1]; // Assuming new row is always last
      if (newRow && inputRefs.current[newRow.id]) {
        inputRefs.current[newRow.id].focus();
      }
    }
    // Update the ref for the next render
    prevCookieRowsLengthRef.current = cookieRows.length;
  }, [cookieRows]); // Depend on cookieRows to run when it changes

  const dndEnabled = !isMobile && cookieRows.length > 1;

  const handleDragStart = (e, id) => {
    setDraggingId(id);
    // Required for Firefox
    try { e.dataTransfer.setData('text/plain', id); } catch (_) { /* noop */ }
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (id !== dragOverId) setDragOverId(id);
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    const sourceId = draggingId;
    setDraggingId(null);
    setDragOverId(null);
    if (!sourceId || sourceId === targetId) return;
    const currentOrder = cookieRows.map(r => r.id);
    const fromIdx = currentOrder.indexOf(sourceId);
    const toIdx = currentOrder.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const newOrder = [...currentOrder];
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, sourceId);
    reorderCookiesById(newOrder);
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverId(null);
  };

  return (
    <div className="cookie-names-section">
      {/* Header with title and plan name */}
      <div className="section-header">
        <h2 className="section-title">Cookie Names</h2>
        {loadedPlanName && <span className="loaded-plan-name">Plan: {loadedPlanName}</span>}
      </div>

      {/* Help text container */}
      <div className="section-help-container">
        <SectionHelp sectionId="cookieNames" isMobile={isMobile} />
      </div>

      {/* Controls: Sort button */}
      <div className="cookie-names-controls">
        {cookieRows.length >= 2 && (
          <button
            type="button"
            onClick={sortCookiesByName}
            className="sort-cookies-button"
            title="Sort cookies alphabetically by name"
          >
            Sort A–Z
          </button>
        )}
        {dndEnabled && (
          <span className="dnd-hint">Tip: drag the handle to reorder.</span>
        )}
      </div>

      <div className="cookie-rows">
        {cookieRows.map((row) => {
          const rowClass =
            'cookie-row' +
            (draggingId === row.id ? ' dragging' : '') +
            (dragOverId === row.id && draggingId && draggingId !== row.id ? ' drag-over' : '');
          return (
            <div
              key={row.id}
              className={rowClass}
              onDragOver={dndEnabled ? (e) => handleDragOver(e, row.id) : undefined}
              onDrop={dndEnabled ? (e) => handleDrop(e, row.id) : undefined}
            >
              {dndEnabled && (
                <span
                  className="drag-handle"
                  draggable
                  onDragStart={(e) => handleDragStart(e, row.id)}
                  onDragEnd={handleDragEnd}
                  title="Drag to reorder"
                  aria-label="Drag to reorder"
                >
                  ⋮⋮
                </span>
              )}
              <input
                type="text"
                value={row.cookieName}
                placeholder="Enter Cookie Name"
                onChange={(e) => {
                  updateCookieRow(row.id, 'cookieName', e.target.value);
                }}
                onFocus={(e) => { const t = e.target; setTimeout(() => t.select(), 0); }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCookieRow();
                  }
                }}
                className="cookie-name-input"
                ref={(el) => (inputRefs.current[row.id] = el)} // Assign ref
              />
              {/* Delete Button */}
              <button
                onClick={() => removeCookieRow(row.id)}
                className="btn-delete"
                title="Remove Row"
                disabled={cookieRows.length <= 1}
                style={{ visibility: cookieRows.length <= 1 ? 'hidden' : 'visible' }}
              >
                <span className="minus-icon">-</span>
                Remove
              </button>
            </div>
          );
        })}

        <button
          onClick={addCookieRow}
          className="btn-create add-cookie-button"
          title="Add Cookie"
        >
          <span className="plus-icon">+</span> Add Cookie
        </button>
      </div>

      {/* Footer with Save/Save As/Clear and Next buttons */}
      {activeSection !== 'all' && (
        <ProCalculatorFooter
          onBack={() => setActiveSection('reference')}
          backLabel="Back: Get Started"
          onNext={() => goToNextSection('cookieNames')}
          nextLabel={skipSets ? 'Next: Size & Shape' : 'Next: Sets'}
        />
      )}
    </div>
  );
};

export default CookieRowsSection;
